// Topbar de actividad del EQUIPO en tres ventanas de CALENDARIO (zona Santiago):
// Día (desde 00:00 hoy), Semana (desde el lunes) y Mes (desde el día 1). Por
// persona: avatar + nombre, y por ventana su icono + nombre + total + el
// desglose de 6 categorías. La leyenda (arriba) da el NOMBRE de cada icono una
// sola vez; los iconos están agrupados y son distintos para no confundir:
//   · Issues → círculos (abierto / cerrado)
//   · PRs → forma pull-request (abierto / cerrado / merge)
//   · Commits → git-commit
// Los 3 miembros (Luis, Ismael, Jael) SIEMPRE aparecen (pre-seed en useContributors).

import { useContributors, type ContributorStats, type WindowStats, type CounterKey } from '../queries'
import {
  CircleDot, CircleCheck, GitPullRequest, GitPullRequestClosed, GitMerge, GitCommitHorizontal,
  Sun, CalendarRange, CalendarDays, type LucideIcon,
} from 'lucide-react'

interface CounterDef {
  key: CounterKey
  label: string
  Icon: LucideIcon
  className: string
}

// Iconos distintos por dominio + color propio (no se repiten, no confunden).
const COUNTERS: CounterDef[] = [
  { key: 'issuesOpened', label: 'Issues abiertos', Icon: CircleDot,            className: 'text-sky-400' },
  { key: 'issuesClosed', label: 'Issues cerrados', Icon: CircleCheck,          className: 'text-emerald-400' },
  { key: 'prsOpened',    label: 'PR abiertos',     Icon: GitPullRequest,       className: 'text-blue-400' },
  { key: 'prsClosed',    label: 'PR cerrados',     Icon: GitPullRequestClosed, className: 'text-rose-400' },
  { key: 'prsMerged',    label: 'Merges',          Icon: GitMerge,             className: 'text-purple-400' },
  { key: 'commits',      label: 'Commits',         Icon: GitCommitHorizontal,  className: 'text-amber-400' },
]

const WINDOWS: { key: 'day' | 'week' | 'month'; label: string; Icon: LucideIcon }[] = [
  { key: 'day',   label: 'Día',    Icon: Sun },
  { key: 'week',  label: 'Semana', Icon: CalendarRange },
  { key: 'month', label: 'Mes',    Icon: CalendarDays },
]

export function ContributorActivity() {
  const stats = useContributors()

  return (
    <section
      data-testid="contributor-activity"
      className="bg-zinc-900/40 border-b border-zinc-800 px-4 4k:px-8 py-1 4k:py-1.5 shrink-0"
    >
      <div className="flex items-center justify-between gap-4 mb-1">
        <p className="shrink-0 text-[9px] 4k:text-[11px] uppercase tracking-widest text-zinc-500">
          Actividad del equipo · mes calendario
        </p>
        {/* Leyenda: icono + nombre de cada categoría (mostrado una sola vez). */}
        <div className="flex flex-wrap items-center justify-end gap-x-2.5 4k:gap-x-4 gap-y-0.5 text-[9px] 4k:text-[11px] text-zinc-500">
          {COUNTERS.map((c) => (
            <span key={c.key} className="flex items-center gap-1">
              <c.Icon className={`h-3 w-3 4k:h-3.5 4k:w-3.5 ${c.className}`} />
              {c.label}
            </span>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-x-6 4k:gap-x-10 gap-y-1">
        {stats.map((s) => (
          <ContributorCard key={s.login} stat={s} />
        ))}
      </div>
    </section>
  )
}

function ContributorCard({ stat }: { stat: ContributorStats }) {
  return (
    <div className="min-w-0" data-testid={`contrib-${stat.login}`}>
      <div className="flex items-center gap-2 mb-0.5">
        <img
          src={`https://github.com/${stat.login}.png?size=48`}
          alt=""
          className="h-5 w-5 4k:h-7 4k:w-7 rounded-full bg-zinc-800 object-cover shrink-0"
        />
        <span className="text-xs 4k:text-sm font-bold text-zinc-100 truncate">{stat.name}</span>
      </div>
      {WINDOWS.map((w) => (
        <WindowRow key={w.key} login={stat.login} winKey={w.key} label={w.label} Icon={w.Icon} stats={stat[w.key]} />
      ))}
    </div>
  )
}

function WindowRow({
  login,
  winKey,
  label,
  Icon,
  stats,
}: {
  login: string
  winKey: 'day' | 'week' | 'month'
  label: string
  Icon: LucideIcon
  stats: WindowStats
}) {
  return (
    <div className="flex items-center gap-2 4k:gap-3" data-testid={`contrib-${login}-${winKey}`}>
      <span className="flex items-center gap-1 w-16 4k:w-24 shrink-0 text-[9px] 4k:text-[11px] uppercase tracking-wide text-zinc-400">
        <Icon className="h-3 w-3 4k:h-4 4k:w-4 shrink-0" />
        {label}
      </span>
      <span
        className="w-6 4k:w-10 shrink-0 text-right font-mono font-black tabular-nums text-zinc-100 text-xs 4k:text-lg"
        data-testid={`contrib-${login}-${winKey}-total`}
      >
        {stats.total}
      </span>
      <span className="flex items-center gap-1.5 4k:gap-2 ml-auto text-[10px] 4k:text-xs">
        {COUNTERS.map((c) => (
          <Counter key={c.key} login={login} winKey={winKey} value={stats[c.key]} label={c.label} Icon={c.Icon} className={c.className} counterKey={c.key} />
        ))}
      </span>
    </div>
  )
}

function Counter({
  login,
  winKey,
  value,
  label,
  Icon,
  className,
  counterKey,
}: {
  login: string
  winKey: string
  value: number
  label: string
  Icon: LucideIcon
  className: string
  counterKey: string
}) {
  return (
    <span
      className={`flex items-center gap-0.5 ${value === 0 ? 'opacity-40' : ''}`}
      data-testid={`contrib-${login}-${winKey}-${counterKey}`}
      title={`${label}: ${value}`}
    >
      <Icon className={`h-2.5 w-2.5 4k:h-3 4k:w-3 ${className}`} />
      <span className="font-mono text-zinc-200">{value}</span>
    </span>
  )
}
